import React, { useState, useEffect } from 'react';
import {
  FaSave,
  FaInfoCircle,
  FaCloudUploadAlt,
  FaSpinner,
  FaCheckCircle,
  FaExclamationCircle,
  FaImage,
  FaTimes,
  FaEye,
  FaBullseye,
  FaUserTie,
} from 'react-icons/fa';
import { useAdmin } from '../context/AdminContext';
import { MEDIA_BASE_URL } from '../../config';
import './SectionManager.css';
import './AboutManager.css';

const DESC_LIMIT = 400;

// 🎯 PURE JS CANVAS IMAGE COMPRESSOR
const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    if (file.size <= 500 * 1024) return resolve(file);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let { width, height } = img;
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error('Canvas is empty'));
            resolve(
              new File(
                [blob],
                file.name.replace(/\.[^/.]+$/, '') + '.jpg',
                { type: 'image/jpeg', lastModified: Date.now() }
              )
            );
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const AboutManager = () => {
  const { data, updateSection, loading } = useAdmin();

  const [form, setForm] = useState({
    title: '',
    description: '',
    vision: '',
    mission: '',
    ownerName: '', // 👈 State for Owner Name
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isCompressing, setIsCompressing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [sizeInfo, setSizeInfo] = useState({ original: '', compressed: '' });

  useEffect(() => {
    if (data?.about) {
      setForm({
        title: data.about.title || '',
        description: data.about.description || '',
        vision: data.about.vision || '',
        mission: data.about.mission || '',
        ownerName: data.about.ownerName || '', // 👈 Loading existing Owner Name
      });
      if (data.about.aboutImage) {
        const imgUrl = data.about.aboutImage.startsWith('http')
          ? data.about.aboutImage
          : `${MEDIA_BASE_URL}${data.about.aboutImage}`;
        setImagePreview(imgUrl);
      }
    }
  }, [data?.about]);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file (JPG, PNG, WEBP).');
      return;
    }

    try {
      setIsCompressing(true);
      const originalSizeFormatted = formatFileSize(file.size);
      const compressed = await compressImage(file);
      const compressedSizeFormatted = formatFileSize(compressed.size);

      setImageFile(compressed);
      setImagePreview(URL.createObjectURL(compressed));
      setSizeInfo({
        original: originalSizeFormatted,
        compressed: compressedSizeFormatted,
      });
      setError('');
    } catch (err) {
      console.error('Compression Error:', err);
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    } finally {
      setIsCompressing(false);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setSizeInfo({ original: '', compressed: '' });
    const input = document.getElementById('about-image-input');
    if (input) input.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if ((form.description || '').length > DESC_LIMIT) {
      setError(`Description cannot exceed ${DESC_LIMIT} characters.`);
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form };
      if (imageFile) payload.imageFile = imageFile;

      await updateSection('about', payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('About save error:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const currentDescLength = (form.description || '').length;

  if (loading && !data?.about?.title) {
    return (
      <div className="sg-manager">
        <div className="ab-loader">
          <FaSpinner className="spin" />
          <p>Loading about content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sg-manager">
      {/* ═══════════════════════════════════════════
          PAGE HEADER
         ═══════════════════════════════════════════ */}
      <div className="sg-page-header">
        <div className="sg-ph-left">
          <div className="sg-ph-icon">
            <FaInfoCircle />
          </div>
          <div className="sg-ph-text">
            <h2>About Us Content</h2>
            <p>Manage your group's legacy, vision, mission, and corporate identity</p>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {saved && (
        <div className="sg-alert sg-alert-success">
          <FaCheckCircle /> About Us page content saved successfully!
        </div>
      )}

      {error && (
        <div className="sg-alert sg-alert-error">
          <FaExclamationCircle /> {error}
        </div>
      )}

      {/* ═══════════════════════════════════════════
          MAIN CONTENT - SPLIT LAYOUT
         ═══════════════════════════════════════════ */}
      <div className="ab-split-layout">
        {/* LEFT: FORM CARD */}
        <div className="sg-form-card ab-form-block">
          <div className="sg-form-header">
            <div>
              <h3>Corporate Philosophy</h3>
              <p>Update core values, vision, and mission statement</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="sg-form">
            {/* Title */}
            <div className="sg-field">
              <label>Page Title <span className="req">*</span></label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Building Trust. Creating Value."
                required
              />
            </div>

            {/* Owner Name Field */}
            <div className="sg-field">
              <label>
                <FaUserTie style={{ color: '#c9a227', marginRight: '6px' }} /> Owner / Founder Name <span className="req">*</span>
              </label>
              <input
                type="text"
                value={form.ownerName}
                onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
                placeholder="e.g. Mr. Rajesh Mehta"
                required
              />
            </div>

            {/* Description */}
            <div className="sg-field">
              <label className="sg-label-flex">
                <span>Description <span className="req">*</span></span>
                <span
                  className={`sg-char-count ${
                    currentDescLength >= DESC_LIMIT ? 'over' : ''
                  }`}
                >
                  {currentDescLength}/{DESC_LIMIT} characters
                </span>
              </label>
              <textarea
                value={form.description}
                onChange={(e) =>
                  setForm({
                    ...form,
                    description: e.target.value.substring(0, DESC_LIMIT),
                  })
                }
                rows="5"
                maxLength={DESC_LIMIT}
                placeholder="Main corporate description about Sarwa Group's establishment..."
                required
              />
            </div>

            {/* Vision + Mission */}
            <div className="sg-field">
              <label><FaEye style={{ color: '#c9a227', marginRight: '6px' }} /> Our Vision</label>
              <textarea
                value={form.vision}
                onChange={(e) => setForm({ ...form, vision: e.target.value })}
                rows="3"
                placeholder="To build businesses that shape a strong, trustworthy future..."
              />
            </div>

            <div className="sg-field">
              <label><FaBullseye style={{ color: '#c9a227', marginRight: '6px' }} /> Our Mission</label>
              <textarea
                value={form.mission}
                onChange={(e) => setForm({ ...form, mission: e.target.value })}
                rows="3"
                placeholder="Transparency, honesty, and quality are the key factors..."
              />
            </div>

            {/* Save Button */}
            <div className="sg-form-actions">
              <button
                type="submit"
                className="sg-btn-gold ab-save-btn"
                disabled={saving || isCompressing}
              >
                {saving ? (
                  <>
                    <FaSpinner className="spin" /> Saving...
                  </>
                ) : (
                  <>
                    <FaSave /> Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* RIGHT: IMAGE UPLOAD PANEL */}
        <div className="ab-image-panel">
          <div className="sg-form-card ab-image-card">
            <div className="sg-form-header">
              <div>
                <h3>Corporate Asset</h3>
                <p>Upload Owner / Founder Portrait Photo</p>
              </div>
            </div>

            {/* Image Preview Area */}
            <div className="ab-image-preview">
              {imagePreview ? (
                <div className="ab-preview-wrap">
                  <img src={imagePreview} alt="Owner Portrait" />
                  <div className="ab-preview-overlay">
                    <div className="ab-preview-info">
                      <FaCheckCircle />
                      <span>Owner Portrait is active</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="ab-empty-preview">
                  <FaImage />
                  <p>No preview available</p>
                  <span>Recommended: Portrait Photo</span>
                </div>
              )}
            </div>

            {/* Upload Action Group */}
            <div className="ab-upload-actions">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
                id="about-image-input"
                disabled={isCompressing || saving}
              />

              <label
                htmlFor="about-image-input"
                className={`ab-upload-btn ${isCompressing ? 'disabled' : ''}`}
              >
                {isCompressing ? (
                  <>
                    <FaSpinner className="spin" /> Compressing...
                  </>
                ) : (
                  <>
                    <FaCloudUploadAlt />
                    {imagePreview ? 'Change Portrait Photo' : 'Choose Portrait Photo'}
                  </>
                )}
              </label>

              {imageFile && (
                <button
                  type="button"
                  className="ab-remove-btn"
                  onClick={removeImage}
                  disabled={isCompressing}
                >
                  <FaTimes /> Remove
                </button>
              )}
            </div>

            {/* Optimization feedback card */}
            {sizeInfo.compressed && (
              <div className="ab-size-info">
                <FaCheckCircle />
                <div>
                  <strong>Optimized for Web</strong>
                  <span>
                    {sizeInfo.original} → {sizeInfo.compressed}
                  </span>
                </div>
              </div>
            )}

            {/* Hint Notice */}
            <div className="ab-info-note">
              <strong>Tip:</strong> This image represents the Founder / MD. 
              Upload a clear face portrait for the best professional look.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutManager;